/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {Helmet} from 'react-helmet';
import PropTypes from 'prop-types';

import {BrandLogo, FileIcon} from '@salesforce/retail-react-app/app/components/icons';
import {
  Box, Button, Flex, Heading, IconButton, Stack, Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
  const {message, stack} = props;

  const title = "This page isn't working";

  return (
    <Flex direction="column" flex={1} id="sf-app" minWidth="375px">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Box as="header" backgroundColor="white" boxShadow="base" width="full">
        <Box
          marginLeft="auto"
          marginRight="auto"
          maxWidth="container.xxxl"
          paddingBottom={[3, 3, 2, 4]}
          paddingTop={[1, 1, 2, 4]}
          px={[4, 4, 6, 8]}
        >
          <IconButton
            aria-label="logo"
            icon={<BrandLogo height={[6, 6, 6, 8]} width={[8, 8, 8, 12]} />}
            marginBottom={[1, 1, 2, 0]}
            variant="unstyled"
            // We need to use window.location.href here rather than history
            // as the application is in an error state. We need to force a
            // hard navigation to get back to the normal state.
            onClick={() => (window.location.href = '/')}
          />
        </Box>
      </Box>
      <Box
        as="main"
        flex={1}
        id="app-main"
        layerStyle="page"
        padding={{lg: 8, md: 6, sm: 0, base: 0}}
        role="main"
      >
        <Flex
          direction="column"
          justify="center"
          px={{base: 4, md: 6, lg: 50}}
          py={{base: 20, md: 24}}
        >
          <Flex align="center" direction="column">
            <FileIcon boxSize={['30px', '32px']} mb={8} />
            <Heading as="h2" fontSize={['xl', '2xl', '2xl', '3xl']} mb={2}>
              {title}
            </Heading>
            <Box marginBottom={8} maxWidth="440px">
              <Text align="center">
                An error has occurred. Try refreshing the page or if you need
                immediate help please contact support.
              </Text>
              {message && (
                <Box
                  as="pre"
                  background="gray.50"
                  borderColor="gray.200"
                  borderStyle="solid"
                  borderWidth="1px"
                  fontSize="sm"
                  mt={4}
                  overflow="auto"
                  padding={4}
                >
                  {message}
                </Box>
              )}
            </Box>
            <Stack direction={['column', 'row']} spacing={4} width={['100%', 'auto']}>
              <Button
                as="a"
                bg="white"
                borderColor="gray.200"
                href="https://help.salesforce.com/s/support"
                target="_blank"
                variant="outline"
              >
                Contact Support
              </Button>
              <Button onClick={() => window.location.reload()}>
                Refresh the page
              </Button>
            </Stack>
          </Flex>
          {stack && (
            <Box marginTop={20}>
              <Text fontSize="md" fontWeight="bold">
                Stack Trace
              </Text>
              <Box
                as="pre"
                background="gray.50"
                borderColor="gray.200"
                borderStyle="solid"
                borderWidth="1px"
                fontSize="sm"
                mt={4}
                overflow="auto"
                padding={4}
              >
                {stack}
              </Box>
            </Box>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

Error.propTypes = {
  // JavaScript error stack trace: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
  stack: PropTypes.string,
  // HTTP status code: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
  status: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
  // A description of the error, if available
  message: PropTypes.string,
};

export default Error;
